import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  Add as AddIcon,
  Archive as ArchiveIcon,
  ContentCopy as ContentCopyIcon,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { programsApi, PROGRAM_LEVELS, type Program, type ProgramStatus } from '@/api/programs'
import { getErrorMessage } from '@/utils/error'
import { useSubscriptionParameterFresh } from '@/hooks/useSubscriptionParameterFresh'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'

/** Строк в скелетоне: столько же, сколько обычно влезает на экран без прокрутки */
const SKELETON_ROWS = [1, 2, 3, 4, 5]

/**
 * Библиотека программ обучения учителя: свои шаблоны плюс системные программы
 * платформы (их можно назначить и скопировать, но не править).
 */
export function ProgramsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [programs, setPrograms] = useState<Program[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Дебаунс поиска: без него каждый символ уходил отдельным запросом в список
  const [searchQuery, setSearchQuery] = useState('')
  // По умолчанию учитель видит только свою библиотеку: раньше вкладка «Все»
  // подмешивала в неё витрину платформы, и свои программы терялись среди чужих.
  // Витрина показывается по галочке — GET /programs без source отдаёт и то, и другое.
  const [showSystem, setShowSystem] = useState(false)
  // Статус описан у GET /programs — единственный фильтр страницы, поэтому он и
  // живёт под кнопкой «Фильтры». Пусто = как было раньше: активные вперемешку с архивом
  const [status, setStatus] = useState<ProgramStatus | ''>('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(15)
  const [total, setTotal] = useState(0)

  // Гейт готового контента платформы: замок и CTA вместо молчаливого 403.
  // Админа гейт не касается — подписки у него нет, и без этой ветки он видел бы
  // замок на контенте, который сам же и ведёт.
  const isAdmin = useAuthStore((state) => state.user?.roles?.some((role) => role.slug === 'admin') ?? false)
  const systemProgramsEnabled = useSubscriptionParameterFresh('system_programs_enabled', !isAdmin) || isAdmin

  // Гейт мог выключиться уже после того, как галочку поставили: в этом случае
  // список обязан вернуться к своим программам, а не остаться на витрине
  const systemVisible = showSystem && systemProgramsEnabled

  // В счёт идут только фильтры панели: поиск виден сам по себе отдельным полем
  const activeFiltersCount = useMemo(
    () => (status ? 1 : 0) + (systemVisible ? 1 : 0),
    [status, systemVisible],
  )

  // Для пустой выдачи важен не счётчик панели, а сам факт сужения списка: у учителя
  // может быть 40 программ, и без этой развилки поиск без совпадений подписывался
  // «Программ пока нет. Создайте первую…» — читалось как потеря библиотеки.
  // Галочка «системные» список расширяет, а не сужает, поэтому в развилку не входит.
  const hasNarrowingFilters = Boolean(searchQuery) || Boolean(status)

  const [createOpen, setCreateOpen] = useState(false)
  // Диалог закрывается сбросом: иначе при повторном открытии он показывает
  // прошлый ввод и прошлую ошибку
  const closeCreate = () => {
    setCreateOpen(false)
    setNewName('')
    setNewLevel('')
    setNewAutoUpdate(true)
    setCreateError(null)
  }
  const [newName, setNewName] = useState('')
  const [newLevel, setNewLevel] = useState('')
  const [newAutoUpdate, setNewAutoUpdate] = useState(true)
  const [saving, setSaving] = useState(false)
  // Отдельно от страничной ошибки: Alert страницы рисуется ПОД открытым
  // диалогом, и 403 «исчерпан лимит программ» пользователь не видел вовсе.
  const [createError, setCreateError] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuProgram, setMenuProgram] = useState<Program | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await programsApi.list({
        q: searchQuery || undefined,
        // Отсутствие source = «свои и витрина»; выключенная галочка сужает до своих
        source: systemVisible ? undefined : 'own',
        status: status || undefined,
        page: page + 1,
        per_page: perPage,
      })
      setPrograms(result.data)
      setTotal(result.meta?.total ?? result.data.length)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [searchQuery, systemVisible, status, page, perPage, t])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(search.trim())
      setPage(0)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    void load()
  }, [load])

  // Страница за пределами выдачи: последнюю программу с неё убрали в архив,
  // сняли галочку «показывать системные» или удалили из другой сессии. MUI на
  // таком page ругается и рисует пустой диапазон, поэтому возвращаемся на
  // последнюю существующую страницу сами
  useEffect(() => {
    if (loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [loading, page, perPage, total])

  const handleCreate = async () => {
    setSaving(true)
    setCreateError(null)
    try {
      const program = await programsApi.create({
        name: newName.trim(),
        level: newLevel || undefined,
        auto_update_default: newAutoUpdate,
      })
      setCreateOpen(false)
      setNewName('')
      setNewLevel('')
      navigate(ROUTES.PROGRAM_VIEW(program.id))
    } catch (err) {
      setCreateError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuProgram(null)
  }

  const handleDuplicate = async (program: Program) => {
    closeMenu()
    try {
      const copy = await programsApi.duplicate(program.id)
      navigate(ROUTES.PROGRAM_VIEW(copy.id))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const handleRestore = async (program: Program) => {
    closeMenu()
    try {
      await programsApi.restore(program.id)
      void load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const handleArchive = async (program: Program) => {
    closeMenu()
    try {
      await programsApi.archive(program.id)
      void load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, program: Program) => {
    // Строка ведёт в программу: без остановки всплытия меню открывало бы её заодно
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuProgram(program)
  }

  /** Шапка таблицы одна и для скелетона, и для данных: разъехаться они не должны */
  const tableHead = (
    <TableHead>
      <TableRow>
        <TableCell>{t('programs.fields.name', 'Название')}</TableCell>
        <TableCell>{t('programs.fields.level', 'Уровень')}</TableCell>
        <TableCell align="right">{t('programs.columnThemes')}</TableCell>
        <TableCell align="right">{t('programs.columnAssignments')}</TableCell>
        <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
      </TableRow>
    </TableHead>
  )

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs items={[{ label: t('programs.title') }]} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('programs.title', 'Программы обучения')}
          </Typography>
          <PageHelpPopover pageKey="programs" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('programs.searchPlaceholder', 'Поиск по названию')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 240, flex: isMobile ? 1 : undefined }}
          />
          <FiltersButton
            open={filtersOpen}
            count={activeFiltersCount}
            onToggle={() => setFiltersOpen((open) => !open)}
            label={t('programs.filtersButton', 'Фильтры')}
          />
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
              onClick={() => setCreateOpen(true)}
            >
              {t('programs.create', 'Создать программу')}
            </Button>
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              select
              size="small"
              label={t('programs.statusFilter', 'Статус')}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as ProgramStatus | '')
                setPage(0)
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('programs.anyStatus', 'Любой статус')}</MenuItem>
              <MenuItem value="active">{t('programs.statusActive', 'Активные')}</MenuItem>
              <MenuItem value="archived">{t('programs.archived', 'В архиве')}</MenuItem>
            </TextField>
            {/* span нужен Tooltip: у выключенного чекбокса нет событий мыши,
                а без подсказки галочка просто «не нажимается» без объяснений */}
            <Tooltip title={systemProgramsEnabled ? '' : t('programs.systemLocked')}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={systemVisible}
                      disabled={!systemProgramsEnabled}
                      onChange={(e) => {
                        setShowSystem(e.target.checked)
                        setPage(0)
                      }}
                    />
                  }
                  label={t('programs.showSystem', 'Показывать системные')}
                />
              </span>
            </Tooltip>
          </Box>
        </Collapse>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {SKELETON_ROWS.map((row) => (
                <Paper key={row}>
                  <Box sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="35%" />
                    <Skeleton variant="text" width="45%" />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                {tableHead}
                <TableBody>
                  {SKELETON_ROWS.map((row) => (
                    <TableRow key={row}>
                      <TableCell>
                        <Skeleton variant="text" width="70%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={60} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="text" width={40} sx={{ ml: 'auto' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="text" width={40} sx={{ ml: 'auto' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : programs.length === 0 ? (
          <Paper>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              {/* Три разные пустоты, и путать их нельзя. Опустевшая страница —
                  не пустая библиотека: программы есть, но на предыдущих
                  страницах. Ничего не нашлось по фильтрам — тоже не пустая
                  библиотека. Призыв создать программу уместен только там, где
                  её действительно нет: иначе учитель по нему заводит дубль
                  вместо того, чтобы сбросить поиск или вернуться на страницу назад */}
              {page > 0 ? (
                <Typography variant="body1" color="text.secondary">
                  {t('programs.emptyPage', 'На этой странице программ не осталось')}
                </Typography>
              ) : hasNarrowingFilters ? (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {t('programs.emptyFiltered')}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ height: 40 }}
                    onClick={() => {
                      setSearch('')
                      setStatus('')
                      setPage(0)
                    }}
                  >
                    {t('common.reset')}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'programs.empty',
                      'Программ пока нет. Создайте первую — она станет шаблоном, который можно назначать ученикам и группам.',
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ height: 40 }}
                    onClick={() => setCreateOpen(true)}
                  >
                    {t('programs.createFirst', 'Создать первую программу')}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {programs.map((program) => (
              <Paper
                key={program.id}
                onClick={() => navigate(ROUTES.PROGRAM_VIEW(program.id))}
                sx={{ cursor: 'pointer' }}
              >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" fontWeight={600}>
                        {program.name}
                      </Typography>
                      {program.is_system && (
                        <Chip size="small" color="primary" label={t('programs.systemBadge', 'EasyA')} />
                      )}
                      {program.is_system && !systemProgramsEnabled && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          icon={<LockIcon fontSize="small" />}
                          label={t('programs.systemLocked')}
                        />
                      )}
                      {program.status === 'archived' && (
                        <Chip size="small" variant="outlined" label={t('programs.archived', 'В архиве')} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t('programs.fields.level', 'Уровень')}: {program.level || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('programs.columnThemes')}: {program.themes_count ?? 0} ·{' '}
                      {t('programs.columnAssignments')}: {program.assignments_count ?? 0}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={t('common.actions', 'Действия')}
                    onClick={(e) => openRowMenu(e, program)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              {tableHead}
              <TableBody>
                {programs.map((program) => (
                  <TableRow
                    key={program.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(ROUTES.PROGRAM_VIEW(program.id))}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" fontWeight={600}>
                          {program.name}
                        </Typography>
                        {program.is_system && (
                          <Chip size="small" color="primary" label={t('programs.systemBadge', 'EasyA')} />
                        )}
                        {program.is_system && !systemProgramsEnabled && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            icon={<LockIcon fontSize="small" />}
                            label={t('programs.systemLocked')}
                          />
                        )}
                        {program.status === 'archived' && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('programs.archived', 'В архиве')}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{program.level || '—'}</TableCell>
                    <TableCell align="right">{program.themes_count ?? 0}</TableCell>
                    <TableCell align="right">{program.assignments_count ?? 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={t('common.actions', 'Действия')}
                        onClick={(e) => openRowMenu(e, program)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Пагинация вне ветки с данными: при каждом перелистывании список
            уходит в загрузку, и внутри ветки контрол исчезал бы вместе с ним —
            выпрыгивая из-под курсора. А со страницы, которая опустела
            (программы заархивировали или сняли галочку «системные»), вернуться
            назад было нечем */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage', 'Строк на странице')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of', 'из')} ${count}`}
          />
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            disabled={Boolean(menuProgram?.is_system) && !systemProgramsEnabled}
            onClick={() => menuProgram && handleDuplicate(menuProgram)}
          >
            <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
            {t('programs.duplicate', 'Сделать копию')}
          </MenuItem>
          {/* Системную программу правит только платформа — учитель работает с копией */}
          {!menuProgram?.is_system && menuProgram?.status !== 'archived' && (
            <MenuItem onClick={() => menuProgram && handleArchive(menuProgram)}>
              <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
              {t('programs.archive', 'В архив')}
            </MenuItem>
          )}
          {!menuProgram?.is_system && menuProgram?.status === 'archived' && (
            <MenuItem onClick={() => menuProgram && handleRestore(menuProgram)}>
              <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
              {t('programs.restore', 'Вернуть из архива')}
            </MenuItem>
          )}
        </Menu>

        <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
          <DialogTitle>{t('programs.create', 'Создать программу')}</DialogTitle>
          <DialogContent>
            {createError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {createError}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label={t('programs.fields.name', 'Название')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            {/* Select, а не свободный ввод: иначе в базе копятся «Какой то уровень»
                и неотличимые друг от друга латинская 'A1' с кириллической 'А1' */}
            <TextField
              select
              fullWidth
              margin="dense"
              label={t('programs.fields.level', 'Уровень')}
              value={newLevel}
              onChange={(e) => setNewLevel(e.target.value)}
            >
              <MenuItem value="">{t('programs.levelNotSet', 'Не указан')}</MenuItem>
              {PROGRAM_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={newAutoUpdate} onChange={(e) => setNewAutoUpdate(e.target.checked)} />}
              label={t('programs.fields.autoUpdateDefault', 'Обновлять копии учеников автоматически')}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
              {t(
                'programs.fields.autoUpdateDefaultHint',
                'Значение по умолчанию для новых назначений; у каждого назначения его можно переключить',
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreate}>{t('common.cancel', 'Отмена')}</Button>
            <Button variant="contained" disabled={!newName.trim() || saving} onClick={handleCreate}>
              {t('common.create', 'Создать')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
